import { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Shield, Send, Paperclip, X, Upload, MessageCircle, Trash2, AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

export default function EmpresaAcompanhamento() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [empresa, setEmpresa] = useState(null);
  const [denuncia, setDenuncia] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [novaMensagem, setNovaMensagem] = useState("");
  const [files, setFiles] = useState([]);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  const protocolo = searchParams.get("protocolo") || "";
  const senha = searchParams.get("senha") || "";

  useEffect(() => {
    if (protocolo && senha) {
      fetchDenuncia();
    } else {
      navigate(`/${slug}`);
    }
  }, [protocolo, senha, slug]);

  useEffect(() => {
    scrollToBottom();
  }, [denuncia?.mensagens]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchDenuncia = async () => {
    try {
      // First check empresa
      const empresaRes = await axios.get(`${API_URL}/empresas/${slug}/check`);
      if (!empresaRes.data.exists) {
        navigate("/");
        return;
      }
      setEmpresa(empresaRes.data);

      // Then fetch denuncia
      const res = await axios.post(`${API_URL}/empresas/${slug}/denuncias/acompanhar`, {
        protocolo,
        senha,
      });
      setDenuncia(res.data);
    } catch (error) {
      toast.error("Protocolo ou senha inválidos");
      navigate(`/${slug}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!novaMensagem.trim()) return;

    setSending(true);
    try {
      await axios.post(`${API_URL}/empresas/${slug}/denuncias/mensagem`, {
        protocolo,
        senha,
        conteudo: novaMensagem,
      });
      
      setNovaMensagem("");
      await fetchDenuncia();
      toast.success("Mensagem enviada");
    } catch (error) {
      console.error("Erro ao enviar mensagem:", error);
      toast.error(error.response?.data?.detail || "Erro ao enviar mensagem");
    } finally {
      setSending(false);
    }
  };

  const handleFileSelect = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    
    for (const file of selectedFiles) {
      const ext = file.name.split('.').pop().toLowerCase();
      const allowed = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'png', 'jpg', 'jpeg', 'gif', 'txt'];
      
      if (!allowed.includes(ext)) {
        toast.error(`Arquivo ${file.name} não permitido`);
        continue;
      }
      
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`Arquivo ${file.name} muito grande (máx. 10MB)`);
        continue;
      }

      const formData = new FormData();
      formData.append("file", file);
      formData.append("protocolo", protocolo);
      formData.append("senha", senha);

      try {
        await axios.post(`${API_URL}/empresas/${slug}/denuncias/acompanhar/anexo`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        toast.success(`Arquivo ${file.name} enviado`);
      } catch (error) {
        toast.error(`Erro ao enviar ${file.name}`);
      }
    }
    
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDeleteRequest = async () => {
    if (deleteConfirmText !== "CONFIRMAR") {
      toast.error("Digite 'CONFIRMAR' para prosseguir");
      return;
    }

    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/empresas/${slug}/denuncia/esquecimento`, {
        data: {
          protocolo,
          senha,
          confirmacao: "CONFIRMAR"
        }
      });
      
      toast.success("Seus dados foram excluídos permanentemente");
      setShowDeleteModal(false);
      navigate(`/${slug}`);
    } catch (error) {
      console.error("Erro ao excluir dados:", error);
      toast.error(error.response?.data?.detail || "Erro ao processar solicitação");
    } finally {
      setDeleting(false);
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      recebida: "Recebida",
      em_analise: "Em Análise",
      investigacao: "Em Investigação",
      concluida: "Concluída",
      arquivada: "Arquivada",
    };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      recebida: "bg-blue-100 text-blue-700",
      em_analise: "bg-yellow-100 text-yellow-700",
      investigacao: "bg-purple-100 text-purple-700",
      concluida: "bg-green-100 text-green-700",
      arquivada: "bg-slate-100 text-slate-700",
    };
    return colors[status] || "bg-slate-100 text-slate-700";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1a1a2e]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
      {/* Header */}
      <header className="bg-[#1a1a2e] text-white">
        <div className="max-w-3xl mx-auto px-6 py-4">
          <Link to={`/${slug}`} className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4" />
            Voltar
          </Link>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8" />
              <div>
                <h1 className="text-xl font-bold">Acompanhamento</h1>
                <p className="text-sm text-slate-300">Protocolo: {protocolo}</p>
              </div>
            </div>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(denuncia?.status)}`}>
              {getStatusLabel(denuncia?.status)}
            </span>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-3xl mx-auto w-full px-6 py-6 flex flex-col">
        {/* Info Card */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-slate-500">Categoria:</span>
              <p className="font-medium capitalize">{denuncia?.categoria}</p>
            </div>
            <div>
              <span className="text-slate-500">Data:</span>
              <p className="font-medium">
                {new Date(denuncia?.created_at).toLocaleDateString('pt-BR')}
              </p>
            </div>
          </div>
        </div>

        {/* Chat Area */}
        <div className="flex-1 bg-white rounded-xl border border-slate-200 flex flex-col overflow-hidden">
          <div className="p-4 border-b border-slate-200">
            <h2 className="font-semibold text-[#1a1a2e] flex items-center gap-2">
              <MessageCircle className="w-5 h-5" />
              Comunicação
            </h2>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 max-h-[400px]">
            {/* Original Description */}
            <div className="flex justify-end">
              <div className="max-w-[80%] bg-[#1a1a2e] text-white rounded-2xl rounded-br-md p-4">
                <p className="text-xs text-slate-300 mb-1">Denúncia Original</p>
                <p className="text-sm">{denuncia?.descricao}</p>
                <p className="text-xs text-slate-400 mt-2">
                  {new Date(denuncia?.created_at).toLocaleString('pt-BR')}
                </p>
              </div>
            </div>

            {/* Messages */}
            {denuncia?.mensagens?.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.remetente === "denunciante" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl p-4 ${
                    msg.remetente === "denunciante"
                      ? "bg-[#1a1a2e] text-white rounded-br-md"
                      : "bg-slate-100 text-[#1a1a2e] rounded-bl-md"
                  }`}
                >
                  {msg.remetente !== "denunciante" && (
                    <p className="text-xs text-slate-500 mb-1">{msg.remetente}</p>
                  )}
                  <p className="text-sm">{msg.conteudo}</p>
                  <p className={`text-xs mt-2 ${
                    msg.remetente === "denunciante" ? "text-slate-400" : "text-slate-500"
                  }`}>
                    {new Date(msg.created_at).toLocaleString('pt-BR')}
                  </p>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="p-4 border-t border-slate-200">
            <div className="flex gap-2">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.txt"
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => fileInputRef.current?.click()}
              >
                <Paperclip className="w-4 h-4" />
              </Button>
              <Textarea
                value={novaMensagem}
                onChange={(e) => setNovaMensagem(e.target.value)}
                placeholder="Digite sua mensagem..."
                className="min-h-[44px] max-h-[120px] resize-none"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
              />
              <Button
                onClick={handleSendMessage}
                disabled={!novaMensagem.trim() || sending}
                className="bg-[#1a1a2e] hover:bg-[#2d2d4a]"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* LGPD - Delete Data Button */}
        <div className="mt-6 p-4 bg-white rounded-xl border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-[#1a1a2e]">Direito ao Esquecimento (LGPD)</h3>
              <p className="text-sm text-slate-500">Solicite a exclusão permanente de todos os seus dados</p>
            </div>
            <Button
              variant="outline"
              onClick={() => setShowDeleteModal(true)}
              className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
              data-testid="lgpd-delete-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Excluir Meus Dados
            </Button>
          </div>
        </div>
      </main>

      {/* LGPD Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <h2 className="text-lg font-bold text-[#1a1a2e]">Confirmar Exclusão de Dados</h2>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-amber-800">
                <strong>Atenção:</strong> Esta ação é <strong>irreversível</strong>. Todos os dados relacionados à sua denúncia serão permanentemente removidos, incluindo:
              </p>
              <ul className="text-sm text-amber-800 mt-2 list-disc list-inside">
                <li>Sua denúncia e descrição</li>
                <li>Todas as mensagens trocadas</li>
                <li>Anexos e arquivos enviados</li>
                <li>Histórico de comunicação</li>
              </ul>
            </div>

            <p className="text-sm text-slate-600 mb-4">
              Para confirmar, digite <strong className="text-red-600">CONFIRMAR</strong> no campo abaixo:
            </p>

            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())}
              placeholder="Digite CONFIRMAR"
              className="w-full px-4 py-2 border border-slate-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-red-500"
              data-testid="lgpd-confirm-input"
            />

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmText("");
                }}
                className="flex-1"
                disabled={deleting}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleDeleteRequest}
                disabled={deleteConfirmText !== "CONFIRMAR" || deleting}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                data-testid="lgpd-confirm-delete-btn"
              >
                {deleting ? "Excluindo..." : "Excluir Permanentemente"}
              </Button>
            </div>

            <p className="text-xs text-slate-400 mt-4 text-center">
              Conforme a Lei Geral de Proteção de Dados (LGPD - Lei nº 13.709/2018)
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
